import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOURNAL_URL = "https://www.viva-artist.com/journal";

const formatDate = (iso, lang) => {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const Journal = () => {
  const { lang, t } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/journal`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, []);

  if (posts.length === 0) return null;
  const [featured, ...rest] = posts;

  return (
    <section id="journal" data-testid="journal-section" className="relative border-t border-gold/15 bg-ink2">
      <div className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.journal.kicker}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-3xl font-light text-parchment sm:text-4xl lg:text-5xl">
            {t.journal.title}
          </h2>
        </Reveal>

        <Reveal delay={0.18}>
          <a
            data-testid="journal-featured-article"
            href={`${JOURNAL_URL}/${featured.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-14 grid grid-cols-1 overflow-hidden border border-gold/20 lg:grid-cols-2"
          >
            <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[420px]">
              <img
                src={featured.cover}
                alt={lang === "fr" ? featured.title_fr : featured.title_en}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent lg:bg-gradient-to-r" />
            </div>
            <div className="flex flex-col justify-center bg-card2 p-8 sm:p-12">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                {formatDate(featured.date, lang)} · {featured.author}
              </p>
              <h3 className="mt-4 font-serif text-3xl font-light leading-tight text-parchment transition-colors duration-300 group-hover:text-goldlight sm:text-4xl">
                {lang === "fr" ? featured.title_fr : featured.title_en}
              </h3>
              <p className="mt-5 text-sm font-light leading-relaxed text-sand sm:text-base">
                {lang === "fr" ? featured.excerpt_fr : featured.excerpt_en}
              </p>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
                {t.journal.read} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </div>
          </a>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {rest.map((post, i) => (
            <Reveal key={post.id} delay={i * 0.1}>
              <a
                data-testid="journal-article-card"
                href={`${JOURNAL_URL}/${post.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-full gap-6 border border-gold/15 bg-card2 p-5 transition-colors duration-500 hover:border-gold/50"
              >
                <div className="relative hidden w-36 shrink-0 overflow-hidden sm:block">
                  <img
                    src={post.cover}
                    alt={post.title_fr}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="flex flex-col justify-center py-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {formatDate(post.date, lang)} · {post.author}
                  </p>
                  <h3 className="mt-3 font-serif text-xl font-light leading-snug text-parchment transition-colors duration-300 group-hover:text-goldlight sm:text-2xl">
                    {lang === "fr" ? post.title_fr : post.title_en}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-sm font-light leading-relaxed text-sand">
                    {lang === "fr" ? post.excerpt_fr : post.excerpt_en}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
                    {t.journal.read} <ArrowUpRight size={12} />
                  </span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Journal;
